import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { FormEvent, useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";

type Mode = "login" | "register";

export function AccountGate({ compact = false }: { compact?: boolean }) {
  const [mode, setMode] = useState<Mode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const login = trpc.auth.login.useMutation();
  const register = trpc.auth.register.useMutation();
  const socialSession = trpc.auth.socialSession.useMutation();
  const handledSocialSession = useRef(false);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  const hasSocialAuth = Boolean(supabaseUrl && supabaseKey);
  const pending = login.isPending || register.isPending || socialSession.isPending;

  useEffect(() => {
    if (!hasSocialAuth) return;
    const supabase = createClient(supabaseUrl!, supabaseKey!);
    let disposed = false;

    const exchangeSession = async (accessToken: string) => {
      if (disposed || handledSocialSession.current) return;
      handledSocialSession.current = true;
      try {
        await socialSession.mutateAsync({ accessToken });
        if (disposed) return;
        await utils.auth.me.invalidate();
        // The app uses its own secure session cookie after the exchange. The
        // provider session is no longer needed in browser storage.
        await supabase.auth.signOut();
      } catch (cause) {
        await supabase.auth.signOut();
        handledSocialSession.current = false;
        setError(cause instanceof TRPCClientError ? cause.message : "Social sign-in could not be completed.");
      }
    };

    const initialise = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) await exchangeSession(data.session.access_token);
    };
    void initialise();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) void exchangeSession(session.access_token);
    });

    return () => {
      disposed = true;
      authListener.subscription.unsubscribe();
    };
  }, [hasSocialAuth, socialSession, supabaseKey, supabaseUrl, utils]);

  const signInWithGoogle = async () => {
    if (!supabaseUrl || !supabaseKey) return;
    setError(null);
    const supabase = createClient(supabaseUrl, supabaseKey);
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (signInError) setError(signInError.message);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setPassword("");
    setConfirmation("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      if (mode === "register") {
        if (password !== confirmation) {
          setError("Passwords do not match.");
          return;
        }
        await register.mutateAsync({ name, email, password });
      } else {
        await login.mutateAsync({ email, password });
      }
      await utils.auth.me.invalidate();
    } catch (cause) {
      if (cause instanceof TRPCClientError) {
        setError(cause.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-safe pb-safe">
      <div className="max-w-md w-full rounded-2xl border border-border/70 bg-card/80 p-6 sm:p-8 shadow-sm space-y-6">
        <div className="space-y-2 text-center">
          {!compact && <h1 className="font-display text-4xl tracking-tight">Daily Groove</h1>}
          <h2 className="text-xl font-semibold">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {mode === "login"
              ? "Sign in to continue with your day."
              : "Start with a private space for the rhythm of your day."}
          </p>
        </div>

        <div className="grid grid-cols-2 rounded-lg bg-muted p-1">
          <button
            type="button"
            onClick={() => switchMode("login")}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === "login" ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => switchMode("register")}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === "register" ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            Create account
          </button>
        </div>

        {hasSocialAuth && (
          <div className="space-y-3">
            <Button type="button" variant="outline" size="lg" className="w-full" disabled={pending} onClick={() => void signInWithGoogle()}>
              Continue with Google
            </Button>
            <p className="text-center text-xs text-muted-foreground">or continue with email</p>
          </div>
        )}

        <form className="space-y-4" onSubmit={submit}>
          {mode === "register" && (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Name</span>
              <input
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                value={name}
                onChange={event => setName(event.target.value)}
                autoComplete="name"
                minLength={2}
                maxLength={120}
                required
              />
            </label>
          )}
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Email</span>
            <input
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              value={email}
              onChange={event => setEmail(event.target.value)}
              type="email"
              autoComplete="email"
              maxLength={320}
              required
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Password</span>
            <input
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              value={password}
              onChange={event => setPassword(event.target.value)}
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={mode === "register" ? 12 : 1}
              maxLength={128}
              required
            />
          </label>
          {mode === "register" && (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Confirm password</span>
              <input
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                value={confirmation}
                onChange={event => setConfirmation(event.target.value)}
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
              />
            </label>
          )}
          {error && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          {mode === "register" && <p className="text-xs text-muted-foreground">Use at least 12 characters. Your password is stored as a secure one-way hash.</p>}
          <Button type="submit" size="lg" className="w-full" disabled={pending}>
            {pending ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>
        <p className="pt-1 text-center text-xs text-muted-foreground">
          <a className="underline-offset-2 hover:text-foreground hover:underline" href="/privacy">
            Privacy Policy
          </a>
          <span aria-hidden="true" className="px-2 text-muted-foreground/50">
            ·
          </span>
          <a className="underline-offset-2 hover:text-foreground hover:underline" href="/terms">
            Terms of Service
          </a>
        </p>
      </div>
    </div>
  );
}
